import type { Signal } from "@suse-o11y/signal";
import React from "react";

import { SkeletonText } from "~/components/skeleton/text";
import { Table } from "~/components/table";
import { TableBody, type TableBodyVirtualize } from "~/components/table/body";
import { TableCell } from "~/components/table/cell";
import type { TableColFilter } from "~/components/table/col-filter";
import type { TableColOrder } from "~/components/table/col-order";
import { TableHead } from "~/components/table/head";
import { TableRow } from "~/components/table/row";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import type { ImmutableMap } from "~/tools/immutable-map";
import { useLastDefined } from "~/tools/use-last-defined";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { OverviewColumnMeta, OverviewColumnMetaDisplay, OverviewRow } from "../../common/openapi";
import { useClosestScrollingContainer } from "../_scrolling-container";
import { Empty } from "../react/empty/Empty";

import { Cell } from "./_cell";
import { FixedColumns } from "./_fixed-columns";
import { HeadCell } from "./_head-cell";

const DEFAULT_COLUMNS_COUNT = 10;
const ESTIMATE_ROW_HEIGHT_PX = 45;

function displayTypeToClass(display: undefined | OverviewColumnMetaDisplay): undefined | string {
	switch (display?._type) {
		case "DurationMetaDisplay":
		case "NumericMetaDisplay":
		case "ReadyStatusMetaDisplay": {
			return cx("justify-end");
		}

		case "HealthMetaDisplay": {
			return cx("justify-center");
		}

		case "MetricMetaDisplay": {
			return display.sparkline ? undefined : cx("justify-end");
		}

		default: {
			return undefined;
		}
	}
}

function displayTypeToColumnTemplate(type: OverviewColumnMetaDisplay["_type"]) {
	switch (type) {
		case "ComponentLinkMetaDisplay": {
			return "minmax(max-content, 1fr)";
		}

		default: {
			return "max-content";
		}
	}
}

const VIRTUALIZE_CONFIG = {
	overscan: 3,
	estimateSize: ESTIMATE_ROW_HEIGHT_PX,
	getItemKey: (row: undefined | OverviewRow, index: number) => row?.componentIdentifier ?? index,
} satisfies TableBodyVirtualize<OverviewRow>;

export interface OverviewTableProps {
	remaining: number;
	timestamp: undefined | Time;
	timeRange: undefined | TimeRange;
	orders: undefined | Signal<ImmutableMap<string, TableColOrder>>;
	filters: undefined | Signal<ImmutableMap<string, TableColFilter>>;
	fixedColumns: undefined | number;
	cols: undefined | ReadonlyArray<OverviewColumnMeta>;
	rows: undefined | ReadonlyArray<OverviewRow>;
	fetchNextPage: undefined | VoidFunction;
	selectedComponentIdentifier?: string;
	onRowClick?: (componentIdentifier: string) => void;
}

export const OverviewTable: React.FC<OverviewTableProps> = ({
	remaining,
	timestamp,
	timeRange,
	orders,
	filters,
	fixedColumns = 0,
	cols,
	rows,
	fetchNextPage,
	selectedComponentIdentifier,
	onRowClick,
}) => {
	const tableRef = React.useRef<React.ComponentRef<typeof Table>>(null);
	const { element } = useClosestScrollingContainer();
	/**
	 * Preserve the columns across loading states to avoid flickering of the table structure.
	 * The columns are expected to be the same for the entire lifecycle of the component, so using the last defined value is sufficient.
	 */
	const columns = useLastDefined(cols);

	return (
		<Table
			borders={["row-t"]}
			className="w-full max-w-full grid-cols-[repeat(var(--columns-count),minmax(max-content,1fr))]"
			paddings={["cell-y-md"]}
			ref={tableRef}
			scrollElement={element}
			style={
				{
					"--columns-count": DEFAULT_COLUMNS_COUNT,
					gridTemplateColumns: columns?.map(({ display }) => displayTypeToColumnTemplate(display._type)).join(" "),
				} as React.CSSProperties
			}
		>
			<TableHead
				className="sticky top-0"
				style={{
					top: (tableRef.current?.offsetTop ?? 0) - (element?.offsetTop ?? 0),
				}}
			>
				<FixedColumns count={DEFAULT_COLUMNS_COUNT} each={columns} fixedColumns={fixedColumns}>
					{(column) => (
						<HeadCell
							className={displayTypeToClass(column?.display)}
							columnId={column?.columnId}
							filters={column?.searchable ? filters : undefined}
							key={column?.columnId}
							orders={column?.sortable ? orders : undefined}
						>
							<SkeletonText when={column?.title} />
						</HeadCell>
					)}
				</FixedColumns>
			</TableHead>

			<TableBody
				each={rows}
				empty={<Empty className="col-span-full" />}
				onRemainingReveal={(visible) => {
					if (visible && fetchNextPage != null) {
						fetchNextPage();
					}
				}}
				remaining={remaining}
				virtualize={VIRTUALIZE_CONFIG}
			>
				{(row) => (
					<TableRow
						isLoading={row == null}
						isSelected={
							onRowClick == null && selectedComponentIdentifier == null
								? undefined
								: row?.componentIdentifier === selectedComponentIdentifier
						}
						onClick={() => {
							if (row != null && onRowClick != null) {
								onRowClick(row.componentIdentifier);
							}
						}}
						role="button"
					>
						<Cells
							columns={columns}
							fixedColumns={fixedColumns}
							row={row}
							timeRange={timeRange}
							timestamp={timestamp}
						/>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
};

const Cells = React.memo<{
	timestamp: undefined | Time;
	timeRange: undefined | TimeRange;
	row: undefined | OverviewRow;
	fixedColumns: undefined | number;
	columns: undefined | ReadonlyArray<OverviewColumnMeta>;
}>(({ timestamp, timeRange, fixedColumns, row, columns }) => (
	<FixedColumns count={DEFAULT_COLUMNS_COUNT} each={columns} fixedColumns={fixedColumns}>
		{(col) => (
			<TableCell className={cx("items-center", displayTypeToClass(col?.display))} key={col?.columnId}>
				<Cell
					cell={col == null ? undefined : row?.cells?.[col.columnId]}
					col={col}
					componentIdentifier={row?.componentIdentifier}
					timeRange={timeRange}
					timestamp={timestamp}
				/>
			</TableCell>
		)}
	</FixedColumns>
));
